import React from "react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { BikeRental } from "../components/BikeRental";
import { Coffee, Wine, Check, Gamepad2 } from "lucide-react";

export default function DomainePage() {
  const { t } = useI18n();
  const { settings } = useSite();
  const g = settings.gallery || [];

  return (
    <div>
      <PageHero title={t("domainePage.title")} subtitle={t("domainePage.subtitle")} image={settings.domaine_hero || "/api/files/begaudiere/uploads/79e164d0-1436-452c-9518-c2a40debe715.webp"} crumb={t("nav.domaine")} />

      {/* Owners presentation */}
      <section className="py-16 lg:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative mx-auto max-w-[260px]">
              <div className="overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-[0_16px_44px_rgba(12,58,82,0.16)]">
                <img
                  src="/api/files/begaudiere/uploads/a144a092-3f9f-40df-be9c-e70a5d0523f7.webp"
                  alt="François et Jessica, vos hôtes au Domaine de la Bégaudière"
                  loading="lazy" decoding="async"
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="owners-photo"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3.5 py-1 text-[11px] font-medium tracking-widest uppercase text-brand mb-4">{t("domainePage.ownersTag")}</span>
            <p className="font-accent italic text-brand text-xl sm:text-2xl mb-2">{t("domainePage.ownersLead")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-4 [text-wrap:balance]">{t("domainePage.ownersTitle")}</h2>
            <p className="font-body text-sm sm:text-base leading-relaxed text-ink-600">{t("domainePage.ownersText")}</p>
            <p className="font-accent text-lg sm:text-xl text-ink-800 mt-5">{t("domainePage.ownersSignature")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-beige/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src={g[1] || settings.hero_image} alt="Le Domaine de la Bégaudière à Mont-Dol, près de Dol-de-Bretagne et du Mont-Saint-Michel" loading="lazy" decoding="async" className="rounded-2xl w-full aspect-[4/3] object-cover" />
          </Reveal>
          <Reveal delay={120}>
            <p className="text-brand font-accent text-2xl italic mb-2">{t("domainePage.histoireTag")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-5 [text-wrap:balance]">{t("domainePage.histoireTitle")}</h2>
            <div className="space-y-4 text-ink-600 font-body text-base sm:text-lg leading-relaxed">
              <p>{t("domainePage.p1")}</p>
              <p>{t("domainePage.p2")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3.5 py-1 text-[11px] font-medium tracking-widest uppercase text-brand mb-4">{t("domainePage.spacesTag")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900">{t("domainePage.spacesTitle")}</h2>
            <p className="mt-3 text-ink-600 font-body">{t("domainePage.spacesLead")}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              [Coffee, t("domainePage.breakfastTitle"), t("domainePage.breakfastText"), (settings.spaces?.breakfast) || "/api/files/begaudiere/uploads/abd83d28-7cfe-4499-8e35-453c340cb754.webp"],
              [Wine, t("domainePage.barTitle"), t("domainePage.barText"), (settings.spaces?.bar) || "/api/files/begaudiere/uploads/7ccadfb7-6673-406c-9390-5642f79b8466.webp"],
              [Gamepad2, t("domainePage.gameTitle"), t("domainePage.gameText"), (settings.spaces?.game) || "/api/files/begaudiere/uploads/8ae94926-002b-462d-b38f-64159689b16f.webp"],
            ].map(([Icon, title, text, img], i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="rounded-2xl overflow-hidden bg-white border border-black/5 h-full">
                  <img src={img} alt={title} loading="lazy" decoding="async" className="w-full aspect-[16/9] object-cover" />
                  <div className="p-7">
                    <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center mb-3"><Icon size={22} strokeWidth={1.5} className="text-brand" /></div>
                    <h3 className="font-heading text-2xl text-ink-900 mb-2">{title}</h3>
                    <p className="text-ink-600 font-body">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900">{t("domainePage.whyTitle")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t("domainePage.why").map((w, i) => (
              <Reveal key={i} delay={(i % 3) * 80}>
                <div className="flex items-start gap-3 bg-white rounded-2xl border border-black/5 p-6 h-full">
                  <span className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center shrink-0"><Check size={16} className="text-brand" /></span>
                  <span className="text-ink-700 font-body">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BikeRental dark />

      {g.length > 0 && (
        <section className="py-16 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl text-ink-900">{t("domainePage.galleryTitle")}</h2>
            </Reveal>
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {g.slice(0, 9).map((img, i) => <img key={i} src={img} alt={`Le Domaine de la Bégaudière — photo ${i + 1}`} loading="lazy" decoding="async" className="w-full rounded-2xl break-inside-avoid" />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
